import React, { useState, useCallback, useEffect, useRef } from 'react';
import { isGeoPackage } from './GisNetbeheerder';
// De toets op het aantal voertuigen staat in de rekenkern, zodat de drempels
// maar op één plek staan.
// De gemeentegedragen wijkindeling. Die wordt op de server uitgerekend, want
// daar staan de sectorvormen en de ingelezen Statbel-gegevens.
import { getWijkindeling } from '../api';
// De groei van het voertuigenpark staat in de rekenkern, zodat er maar een
// groeicijfer in de app bestaat.
import { WELVAARTSINDEX_VLAANDEREN } from '../gemeenteData';

// ── Kleuren ─────────────────────────────────────────────────────────
// Gelijk aan Dashboard.js, AppWithOnboarding.js en GemeenteEditor.js.
const N = {
  ink:'#12161C', ink2:'#3D4652', ink3:'#6B7684', ink4:'#9AA4B1',
  paper:'#FFFFFF', paper2:'#FBFAF7', canvas:'#ECEAE5',
  line:'#D9D5CD', line2:'#C4BFB5', head:'#F6F4F0',
  accent:'#1F5C6B', accent2:'#2D7F92',
  ok:'#3F7D4E', okBg:'#EFF5EE', okBd:'#CFE0CB',
  warn:'#9C6420', warnBg:'#F9F1E4', warnBd:'#E8D3AC',
  red:'#B4442F',
};

// Zelfde drie wijktypes als het gevalideerde rekenmodel (zie Leeswijzer §5).
// Een wijk kan er meerdere aanvinken (bijvoorbeeld woonwijk + bedrijventerrein
// voor een hybride wijk); calcWijk middelt dan de bijbehorende doelgroepenmix.
const WIJKTYPES = [
  { key:'binnenstad',       label:'Binnenstad' },
  { key:'woonwijk',         label:'Woonwijk' },
  { key:'bedrijventerrein', label:'Bedrijventerrein' },
];

// ── Stap-labels ──────────────────────────────────────────────────────
const STAPPEN = ['Gemeente zoeken', 'Wijken en gegevens', 'Wijktype', 'GIS netbeheerder', 'Bevestiging'];

// ── API calls ────────────────────────────────────────────────────────

// Nominatim: gemeente → center, bbox, NIS-code
// Robuuste versie: retry bij falen (Nominatim heeft rate-limit 1/sec + soms
// tijdelijke uitval). Fallback: probeer zonder land-suffix als eerste poging
// niets oplevert.
async function fetchNominatimSingle(query, land) {
  const q = encodeURIComponent(query);
  const url = `https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=5&addressdetails=1&countrycodes=${land === 'België' ? 'be' : 'nl'}`;
  const resp = await fetch(url, { headers:{ 'User-Agent':'Laadkaart/1.0 (info@longhill.eu)' } });
  if (!resp.ok) {
    const err = new Error(`Nominatim HTTP ${resp.status}`);
    err.retryable = resp.status === 429 || resp.status >= 500;
    throw err;
  }
  const data = await resp.json();
  return data;
}

async function fetchNominatim(naam, land) {
  const sleep = (ms) => new Promise(r => setTimeout(r, ms));
  const queries = [`${naam}, ${land}`, naam]; // eerst met land, dan zonder
  let laatsteFout = null;

  for (const query of queries) {
    for (let poging = 0; poging < 3; poging++) {
      try {
        const data = await fetchNominatimSingle(query, land);
        // Filter op municipality/city/town/village en boundary
        const match = data.find(d => ['municipality','city','town','village'].includes(d.type) || d.class === 'boundary')
          || data[0];
        if (match) {
          const bb = match.boundingbox; // [south, north, west, east]
          return {
            center: [parseFloat(match.lat), parseFloat(match.lon)],
            bbox:   [parseFloat(bb[0]), parseFloat(bb[2]), parseFloat(bb[1]), parseFloat(bb[3])],
            displayName: match.display_name.split(',').slice(0,2).join(', '),
            osmId: match.osm_id,
            type:  match.type,
          };
        }
        break; // geen match voor deze query, probeer volgende query
      } catch (e) {
        laatsteFout = e;
        if (!e.retryable) break; // niet-retryable fout, ga naar volgende query
        await sleep(1500); // wacht voor rate-limit
      }
    }
  }

  if (laatsteFout) throw new Error(`Nominatim onbereikbaar (${laatsteFout.message}). Probeer opnieuw over 10-20 seconden.`);
  throw new Error(`Gemeente "${naam}" niet gevonden in OpenStreetMap. Controleer de spelling.`);
}

// Oppervlakte (km2) van een polygoon obv lat/lon-coördinaten, met een lokale
// equirectangulaire projectie (voldoende nauwkeurig op de schaal van een
// gemeente/wijk). Retourneert null als er geen bruikbare geometrie is.
function polygonOppervlakteKm2(coords) {
  if (!coords || coords.length < 3) return null;
  const R = 6371; // aardstraal in km
  const latRad = (coords.reduce((s,c) => s + c.lat, 0) / coords.length) * Math.PI / 180;
  const kmPerDegLat = R * Math.PI / 180;
  const kmPerDegLon = R * Math.cos(latRad) * Math.PI / 180;
  let opp = 0;
  for (let i = 0; i < coords.length; i++) {
    const j = (i + 1) % coords.length;
    const xi = coords[i].lon * kmPerDegLon, yi = coords[i].lat * kmPerDegLat;
    const xj = coords[j].lon * kmPerDegLon, yj = coords[j].lat * kmPerDegLat;
    opp += xi * yj - xj * yi;
  }
  return Math.abs(opp) / 2;
}

// Haalt uit een Overpass-element (way of relation, met "out geom") een
// bruikbare oppervlakte, indien de geometrie dat toelaat. Bij een relation
// (multipolygoon) wordt de "outer"-ring gebruikt, gaten in de polygoon
// worden dus niet afgetrokken, een bewuste, benoemde vereenvoudiging.
function elementOppervlakteKm2(el) {
  if (el.type === 'way' && el.geometry?.length >= 3) {
    return polygonOppervlakteKm2(el.geometry);
  }
  if (el.type === 'relation' && el.members?.length) {
    const outer = el.members.find(m => m.role === 'outer' && m.geometry?.length >= 3);
    if (outer) return polygonOppervlakteKm2(outer.geometry);
  }
  return null;
}

// Haalt de echte oppervlakte van de hele gemeente op via haar eigen
// administratieve grens (indien Overpass die teruggeeft). Valt terug op
// null als dat niet lukt, de aanroeper moet dan zelf een schatting maken
// (bijv. obv de bbox) en dat duidelijk als zodanig markeren.
async function fetchGemeenteOppervlakte(naam) {
  const query = `[out:json][timeout:20];
relation["boundary"="administrative"]["admin_level"~"8|9"]["name"="${naam}"];
out geom;`;
  try {
    const resp = await fetch('https://overpass-api.de/api/interpreter', {
      method:'POST', body:'data='+encodeURIComponent(query),
    });
    if (!resp.ok) return null;
    const json = await resp.json();
    for (const el of (json.elements || [])) {
      const opp = elementOppervlakteKm2(el);
      if (opp) return opp;
    }
  } catch { /* stil falen, aanroeper valt terug op bbox-schatting */ }
  return null;
}

// Echte bevolkingsopzoeking via Wikidata (property P1082 = bevolking), i.p.v.
// de oude, foutief als "Bron: Statbel" gelabelde gok (oppervlakte × dichtheid).
// Filtert op P17=Q31 (land = België) om verwarring met gelijknamige plaatsen
// elders te voorkomen (bijv. Hasselt bestaat ook als buurtnaam in Nederland).
async function fetchGemeenteBevolking(naam) {
  try {
    const zoekUrl = `https://www.wikidata.org/w/api.php?action=wbsearchentities&search=${encodeURIComponent(naam)}&language=nl&format=json&type=item&origin=*&limit=5`;
    const zoekResp = await fetch(zoekUrl);
    if (!zoekResp.ok) return null;
    const zoekData = await zoekResp.json();
    if (!zoekData.search?.length) return null;

    for (const kandidaat of zoekData.search) {
      const entityUrl = `https://www.wikidata.org/wiki/Special:EntityData/${kandidaat.id}.json`;
      const entityResp = await fetch(entityUrl);
      if (!entityResp.ok) continue;
      const entityData = await entityResp.json();
      const claims = entityData.entities?.[kandidaat.id]?.claims;
      const land = claims?.P17?.[0]?.mainsnak?.datavalue?.value?.id;
      if (land !== 'Q31') continue; // niet België, overslaan
      const bevolkingClaims = claims?.P1082;
      if (!bevolkingClaims?.length) continue;
      // Meest recente bevolkingsopgave (P1082 kan meerdere tijdstippen bevatten)
      const meestRecent = bevolkingClaims.reduce((best, c) => {
        const datum = c.qualifiers?.P585?.[0]?.datavalue?.value?.time || '';
        return (!best || datum > best.datum) ? { datum, waarde: c.mainsnak?.datavalue?.value?.amount } : best;
      }, null);
      const aantal = meestRecent?.waarde ?? bevolkingClaims[0].mainsnak?.datavalue?.value?.amount;
      if (aantal) return Math.abs(parseInt(aantal));
    }
  } catch { /* stil falen, aanroeper valt terug op de oude schatting */ }
  return null;
}

// Overpass: haal deelgemeenten/wijken op, mét grenzen waar beschikbaar
// (voor de dekkingsberekening o.b.v. MOW's 250m-norm), anders alleen als punt
// (naam + positie), de oppervlakte wordt dan later proportioneel verdeeld.
async function fetchWijkenViaOverpass(bbox) {
  const [s, w, n, e] = bbox;
  const query = `[out:json][timeout:25];
(
  node["place"~"suburb|neighbourhood|district|quarter"](${s},${w},${n},${e});
  way["place"~"suburb|neighbourhood|quarter"](${s},${w},${n},${e});
  relation["boundary"="administrative"]["admin_level"~"9|10"](${s},${w},${n},${e});
  way["boundary"="administrative"]["admin_level"~"9|10"](${s},${w},${n},${e});
);
out geom;`;
  const resp = await fetch('https://overpass-api.de/api/interpreter', {
    method:'POST', body:'data='+encodeURIComponent(query),
  });
  if (!resp.ok) return [];
  const json = await resp.json();
  return (json.elements || [])
    .filter(el => el.tags?.name)
    .map(el => {
      const isPoly = el.type === 'way' || el.type === 'relation';
      let lat = el.lat, lng = el.lon, oppervlakteKm2 = null;
      if (isPoly) {
        oppervlakteKm2 = elementOppervlakteKm2(el);
        const geom = el.type === 'way' ? el.geometry : el.members?.find(m => m.role === 'outer')?.geometry;
        if (geom?.length) {
          lat = geom.reduce((s,c) => s + c.lat, 0) / geom.length;
          lng = geom.reduce((s,c) => s + c.lon, 0) / geom.length;
        }
      }
      return {
        id:   `WK_${el.id}`,
        naam: el.tags.name,
        lat, lng, oppervlakteKm2,
        type: el.tags.place || el.tags.admin_level || 'wijk',
      };
    })
    .filter(w => w.lat != null && w.lng != null)
    // Zelfde wijk kan als los punt én als polygoon voorkomen (bijv. een
    // "place"-node naast een "boundary"-relation met dezelfde naam); geef
    // de voorkeur aan de versie mét een echte, gemeten oppervlakte.
    .reduce((acc, w) => {
      const bestaand = acc.find(x => x.naam === w.naam);
      if (!bestaand) acc.push(w);
      else if (!bestaand.oppervlakteKm2 && w.oppervlakteKm2) Object.assign(bestaand, w);
      return acc;
    }, [])
    .slice(0, 20); // max 20 wijken
}

// ══════════════════════════════════════════════════════════════════════
// COMPONENT
// ══════════════════════════════════════════════════════════════════════
export default function GemeenteOnboarding({ initialGemeente, onComplete, onClose }) {
  // Als de wizard vanuit het dashboard geopend is met een reeds geselecteerde
  // gemeente, start dan direct met die naam ingevuld en trigger één keer de
  // zoekactie automatisch. Zo hoeft de gebruiker niet opnieuw te typen.
  const [stap,          setStap]         = useState(0);
  // Het GeoPackage van de netbeheerder. Wordt pas ingelezen als de gemeente
  // bestaat, want de server bewaart het per gemeente. Optioneel.
  const [gisBestand,    setGisBestand]   = useState(null);
  const [gisFout,       setGisFout]      = useState('');
  const gisKiezer = useRef(null);
  const [zoekNaam,      setZoekNaam]     = useState(initialGemeente?.naam || '');
  const [land,          setLand]         = useState('België');
  const [loading,       setLoading]      = useState(false);
  const [error,         setError]        = useState('');

  // Gemeente data
  const [geoData,       setGeoData]      = useState(null);
  // Waar de wijken vandaan komen, zodat het scherm dat kan tonen in plaats
  // van stilzwijgend nullen te laten zien.
  const [indelingBron,    setIndelingBron]    = useState(null);
  const [indelingMelding, setIndelingMelding] = useState(null);
  // Waar het aantal voertuigen vandaan komt. Leeg betekent: nog de schatting.
  const [voertuigenHerkomst, setVoertuigenHerkomst] = useState(null);
  const [wijken,        setWijken]       = useState([]);
  const [gemeenteNaam,  setGemeenteNaam] = useState('');
  const [inwoners,      setInwoners]     = useState('');
  const [voertuigen,    setVoertuigen]   = useState('');
  const [privePct,      setPrivePct]     = useState('50');
  const [gemeenteOppervlakteKm2, setGemeenteOppervlakteKm2] = useState(null);
  const [inwonersIsSchatting, setInwonersIsSchatting] = useState(true);

  // Wijk editing
  const [editIdx,       setEditIdx]      = useState(null);

  const kleur = N.accent;

  // ── Stap 1: zoek gemeente ────────────────────────────────────────
  const zoekGemeente = useCallback(async () => {
    if (!zoekNaam.trim()) return;
    setLoading(true); setError('');
    try {
      setIndelingBron(null); setIndelingMelding(null); setVoertuigenHerkomst(null);
      const geo = await fetchNominatim(zoekNaam.trim(), land);
      setGeoData(geo);
      setGemeenteNaam(zoekNaam.trim());

      // Ruwe bbox-proxy, ALLEEN als allerlaatste terugvaloptie als de echte
      // opzoeking hieronder niets oplevert.
      const latDiff  = geo.bbox[2] - geo.bbox[0];
      const lngDiff  = geo.bbox[3] - geo.bbox[1];
      const km2Schat = Math.round(latDiff * lngDiff * 12000);
      const inwSchatRuw = Math.min(Math.max(km2Schat * 80, 5000), 500000);

      // Echte bevolkingsopzoeking via Wikidata. Dit was voorheen een gok die
      // ten onrechte als "Bron: Statbel" werd getoond; nu een echte, actuele
      // opzoeking, met de oude gok alleen nog als terugvaloptie.
      const echteBevolking = await fetchGemeenteBevolking(zoekNaam.trim());
      const inwSchat = echteBevolking || inwSchatRuw;
      setInwonersIsSchatting(!echteBevolking);
      setInwoners(String(inwSchat));
      // Voertuigenratio obv het gemiddelde van onze vier geverifieerde
      // gemeenten (Leuven 0,459 / Gent 0,354 / Hasselt 0,501, gemiddeld
      // ~0,44), i.p.v. de eerdere, niet onderbouwde 0,45. Blijft een
      // schatting totdat er een echte, per-gemeente voertuigenbron is.
      setVoertuigen(String(Math.round(inwSchat * 0.44 / 100) * 100));

      // Echte gemeente-oppervlakte via Overpass; valt terug op de bbox-
      // schatting hierboven als er geen administratieve grens gevonden wordt
      // (in dat geval blijft de oppervlakte een schatting, niet een meting)
      const gemeenteOppervlakte = (await fetchGemeenteOppervlakte(zoekNaam.trim())) || km2Schat;
      setGemeenteOppervlakteKm2(gemeenteOppervlakte);

      // Eerst de OFFICIELE wijkindeling proberen: de wijken die de gemeente
      // zelf heeft vastgesteld, met inwoners, auto's en oppervlakte gemeten uit
      // de statistische sectoren. Lukt dat niet, bijvoorbeeld buiten Vlaanderen
      // of zolang de Statbel-bronnen niet zijn ingelezen, dan blijft de oude
      // weg via OpenStreetMap gewoon werken.
      let officieel = null;
      try {
        const uit = await getWijkindeling(zoekNaam.trim());
        if (uit?.wijken?.length) officieel = uit;
        else setIndelingMelding(uit?.reden || null);
      } catch (e) {
        setIndelingMelding(`De officiële wijkindeling kon niet worden opgehaald: ${e.message}`);
      }

      if (officieel) {
        // Het gemeten aantal voertuigen uit de bron, zoals geteld. De groei na
        // het jaar van de telling past de rekenkern zelf toe met de huishoudens
        // van de gemeente (besluit HL, 21 oktober 2026); hier bijwerken zou die
        // groei dubbel tellen.
        let vTot = null, vHerkomst = null;
        if (Number.isFinite(officieel.voertuigen) && officieel.voertuigen > 0) {
          const peil = officieel.voertuigenPeildatum ? new Date(officieel.voertuigenPeildatum) : null;
          vTot = Math.round(officieel.voertuigen);
          const uitHuishoudens = officieel.voertuigenBron === 'huishoudbestand';
          const bronnaam = uitHuishoudens
            ? 'Auto\u2019s van gezinnen uit het huishoudbestand van Statbel, plus de auto\u2019s op naam '
              + 'van een bedrijf of een instelling'
            : 'Gemeten aantal uit de bron Voertuigenpark per gemeente';
          vHerkomst = peil
            ? `${bronnaam}. Gemeten op ${peil.toLocaleDateString('nl-BE')}.`
            : `${bronnaam}.`;
          setVoertuigen(String(vTot));
          setVoertuigenHerkomst(vHerkomst);
        }
        setIndelingBron({
          aantal: officieel.wijken.length,
          sectoren: officieel.sectoren,
          inwonersBekend: officieel.inwonersBekend,
          autosBekend: officieel.autosBekend,
        });
        setWijken(officieel.wijken.map(w => ({
          id: w.id,
          naam: w.naam,
          lat: w.lat, lng: w.lng,
          inwoners: w.inwoners,
          // Het aantal voertuigen van de gemeente over de wijken verdelen naar
          // het aantal auto's uit de sectoren. Zijn die niet bekend, dan naar
          // inwonertal; is dat er ook niet, dan gelijk.
          voertuigen: (() => {
            const totA = officieel.wijken.reduce((a, x) => a + (x.autos || 0), 0);
            const totI = officieel.wijken.reduce((a, x) => a + (x.inwoners || 0), 0);
            const deel = totA > 0 ? (w.autos || 0) / totA
                       : (totI > 0 ? (w.inwoners || 0) / totI : 1 / officieel.wijken.length);
            return Math.round((vTot ?? (inwSchat * 0.44)) * deel);
          })(),
          oppervlakteKm2: w.oppervlakteKm2,
          oppervlakteIsProxy: false,
          wijktype: ['woonwijk'],
          ovAandeel: 0,
          actief: true,
        })));
        setStap(1);
        setLoading(false);
        return;
      }

      // Haal wijken op via Overpass
      const wkData = await fetchWijkenViaOverpass(geo.bbox);
      if (wkData.length > 0) {
        const totInwSchat = wkData.length; // gelijk verdeeld als startpunt, hieronder proportioneel over gemeten oppervlaktes waar bekend
        setWijken(wkData.map((w, i) => {
          const wijkInwoners = Math.round(inwSchat / Math.max(wkData.length, 1) / 100) * 100;
          return {
            ...w,
            id:          `NK${String(i+1).padStart(2,'0')}`,
            inwoners:    wijkInwoners,
            voertuigen:  Math.round(inwSchat * 0.45 / Math.max(wkData.length, 1) / 50) * 50,
            // Echte, via Overpass gemeten oppervlakte blijft staan (w.oppervlakteKm2
            // uit fetchWijkenViaOverpass); is die er niet, dan een proxy op basis
            // van inwonersaandeel van de gemeente-oppervlakte, duidelijk als
            // zodanig te herkennen (oppervlakteIsProxy: true) totdat er een
            // echte meting beschikbaar komt.
            oppervlakteKm2: w.oppervlakteKm2 ?? (gemeenteOppervlakte / Math.max(wkData.length, 1)),
            oppervlakteIsProxy: w.oppervlakteKm2 == null,
            wijktype:    ['woonwijk'],
            ovAandeel:   0,
            actief:      true,
          };
        }));
      } else {
        // Fallback: maak 4 standaard wijken
        const kwarten = ['Noord','Zuid','Oost','West'];
        const offsets = [[0.01,0],[-0.01,0],[0,0.01],[0,-0.01]];
        setWijken(kwarten.map((naam, i) => ({
          id:          `NK0${i+1}`,
          naam,
          lat:         geo.center[0] + (offsets[i]?.[0] || 0),
          lng:         geo.center[1] + (offsets[i]?.[1] || 0),
          inwoners:    Math.round(inwSchat / 4 / 100) * 100,
          voertuigen:  Math.round(inwSchat * 0.45 / 4 / 50) * 50,
          oppervlakteKm2: gemeenteOppervlakte / 4,
          oppervlakteIsProxy: true,
          wijktype:    ['woonwijk'],
          ovAandeel:   0,
          actief:      true,
        })));
      }
      setStap(1);
    } catch(e) {
      setError(e.message);
    }
    setLoading(false);
  }, [zoekNaam, land]);

  // Auto-zoeken als de wizard geopend wordt met een voorgeselecteerde gemeente
  // uit het dashboard. Vuurt exact één keer bij mount af, en alleen als er
  // een naam is meegegeven. Voorkomt dubbele triggers via de useRef guard.
  const autoZoekGedaan = useRef(false);
  useEffect(() => {
    if (!initialGemeente?.naam) return;
    if (autoZoekGedaan.current) return;
    autoZoekGedaan.current = true;
    // Wachten op één render zodat state (zoekNaam) is toegepast, dan pas fetch.
    const t = setTimeout(() => { zoekGemeente(); }, 0);
    return () => clearTimeout(t);
  }, [initialGemeente, zoekGemeente]);

  // ── Stap 2: wijktype aan/uit ──────────────────────────────────────
  const toggleWijktype = (idx, key) => {
    setWijken(ws => ws.map((w, i) => {
      if (i !== idx) return w;
      const huidig = w.wijktype || [];
      const heeft = huidig.includes(key);
      const nieuw = heeft ? huidig.filter(t => t !== key) : [...huidig, key];
      return { ...w, wijktype: nieuw.length ? nieuw : ['woonwijk'] }; // nooit helemaal leeg
    }));
  };

  const updateWijk = (idx, field, val) => {
    setWijken(ws => ws.map((w, i) => i === idx ? { ...w, [field]: val } : w));
  };

  const toggleWijk = (idx) => {
    setWijken(ws => ws.map((w, i) => i === idx ? { ...w, actief: !w.actief } : w));
  };

  const addWijk = () => {
    const n = wijken.length + 1;
    setWijken(ws => [...ws, {
      id:`NK${String(n).padStart(2,'0')}`, naam:`Wijk ${n}`,
      lat: geoData.center[0] + (Math.random()-0.5)*0.02,
      lng: geoData.center[1] + (Math.random()-0.5)*0.02,
      inwoners: 5000, voertuigen: 2000,
      wijktype:['woonwijk'], ovAandeel:0, actief:true,
    }]);
  };

  // ── Stap 4: genereer gemeente-object ─────────────────────────────
  const bevestig = () => {
    const actieveWijken = wijken.filter(w => w.actief);
    const id = gemeenteNaam.toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '');
    const gemeente = {
      id, naam: gemeenteNaam,
      provincie: land,
      inwoners:  parseInt(inwoners) || 50000,
      voertuigen:parseInt(voertuigen) || 22000,
      // De welvaartsindex zit in geen enkele berekening meer, maar de kolom
      // bestaat nog. Daarom het Vlaams gemiddelde, in te vullen in het venster
      // Bewerken zodra er een eigen cijfer van Statbel is.
      welvaartsindex:   WELVAARTSINDEX_VLAANDEREN,
      privePctBerekend: (parseFloat(privePct) || 50) / 100,
      oppervlakteKm2:   gemeenteOppervlakteKm2,
      center:    geoData.center,
      zoom:      13,
      bbox:      geoData.bbox,
      kleur,
      wijken:    actieveWijken.map(w => {
        // NUL is sinds de gemeentegedragen wijkindeling een echte waarde: een
        // wijk als "niet te lokaliseren" heeft werkelijk geen inwoners. Met een
        // eenvoudige of-constructie werd die stilletjes 5.000. Daarom expliciet
        // toetsen of er een getal staat.
        const inw = parseInt(w.inwoners, 10);
        const vrt = parseInt(w.voertuigen, 10);
        return {
          id:         w.id,
          naam:       w.naam,
          inwoners:   Number.isFinite(inw) ? inw : 5000,
          voertuigen: Number.isFinite(vrt) ? vrt : 2000,
          wijktype:   w.wijktype && w.wijktype.length ? w.wijktype : ['woonwijk'],
          ovAandeel:  w.ovAandeel || 0,
          oppervlakteKm2: w.oppervlakteKm2 ?? null,
          // Zonder dit veld merkt de server de gemeten oppervlakte aan als
          // schatting, want zijn standaard is "alles wat niet expliciet false is".
          oppervlakteIsProxy: w.oppervlakteIsProxy !== false,
          lat:        w.lat,
          lng:        w.lng,
        };
      }),
    };
    onComplete(gemeente, { gisBestand });
  };

  // ── Stijlen ───────────────────────────────────────────────────────
  const s = {
    overlay:  { position:'fixed', inset:0, background:'rgba(18,22,28,0.45)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 },
    modal:    { width:720, maxHeight:'88vh', background:N.paper, border:`1px solid ${N.line2}`, borderRadius:14, display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 24px 60px rgba(18,22,28,0.24), 0 2px 6px rgba(18,22,28,0.08)', fontFamily:"-apple-system,BlinkMacSystemFont,'Inter','Segoe UI',system-ui,sans-serif" },

    header:   { padding:'17px 24px', borderBottom:`1px solid ${N.line}`, background:`linear-gradient(180deg,#FDFCFA,${N.paper})`, display:'flex', justifyContent:'space-between', alignItems:'center' },
    title:    { fontSize:16, fontWeight:700, color:N.ink, letterSpacing:'-0.015em' },
    subtitle: { fontSize:12.5, color:N.ink4, marginTop:2 },
    close:    { width:28, height:28, borderRadius:7, border:`1px solid ${N.line2}`, background:N.paper, color:N.ink3, cursor:'pointer', fontSize:15, display:'flex', alignItems:'center', justifyContent:'center', lineHeight:1, padding:0, fontFamily:'inherit' },

    // Stappenbalk: afgeronde stappen krijgen een vinkje, komende stappen een cijfer
    steps:    { display:'flex', padding:'0 24px', background:N.head, borderBottom:`1px solid ${N.line}` },
    step:     (actief, klaar) => ({ padding:'12px 0', marginRight:26, fontSize:13.5, fontWeight:600, color: klaar ? N.ok : actief ? N.accent : N.ink4, borderBottom:`2.5px solid ${actief ? N.accent : 'transparent'}`, marginBottom:-1, cursor: klaar ? 'pointer' : 'default', display:'flex', alignItems:'center', gap:7, background:'none', border:'none', borderTop:'none', borderLeft:'none', borderRight:'none', fontFamily:'inherit' }),
    bol:      (actief, klaar) => ({ width:19, height:19, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, flexShrink:0, background: klaar ? N.okBg : actief ? N.accent : N.line, color: klaar ? N.ok : actief ? '#fff' : N.ink4, border: klaar ? `1px solid ${N.okBd}` : 'none' }),

    body:     { flex:1, overflowY:'auto', padding:'20px 24px' },
    footer:   { padding:'14px 24px', borderTop:`1px solid ${N.line}`, background:N.head, display:'flex', justifyContent:'space-between', alignItems:'center' },

    row:      { marginBottom:17 },
    label:    { fontSize:12.5, fontWeight:600, color:N.ink2, marginBottom:6, display:'block' },
    input:    { width:'100%', boxSizing:'border-box', background:N.paper, border:`1px solid ${N.line2}`, borderRadius:8, padding:'9px 12px', color:N.ink, fontSize:14.5, outline:'none', fontFamily:'inherit', fontVariantNumeric:'tabular-nums' },
    // Rand in waarschuwingskleur wanneer een cijfer een schatting is
    inputVast: { width:'100%', boxSizing:'border-box', background:N.canvas, border:`1px solid ${N.line}`, borderRadius:8, padding:'9px 12px', color:N.ink3, fontSize:14.5, outline:'none', fontFamily:'inherit', fontVariantNumeric:'tabular-nums', cursor:'not-allowed' },
    inputLet: { width:'100%', boxSizing:'border-box', background:N.warnBg, border:`1px solid ${N.warnBd}`, borderRadius:8, padding:'9px 12px', color:N.ink, fontSize:14.5, outline:'none', fontFamily:'inherit', fontVariantNumeric:'tabular-nums' },
    // Herkomst van een cijfer: groen bij een echte bron, oranje bij een schatting
    bron:     (soort) => ({ fontSize:12, marginTop:5, lineHeight:1.5, display:'flex', gap:6, alignItems:'flex-start', color: soort==='ok' ? N.ok : soort==='let' ? N.warn : N.ink4 }),

    btn:      (primair) => ({ padding:'9px 19px', borderRadius:8, fontSize:13.5, fontWeight: primair ? 700 : 600, cursor:'pointer', border: primair ? 'none' : `1px solid ${N.line2}`, background: primair ? N.accent : N.paper, color: primair ? '#fff' : N.ink2, fontFamily:'inherit' }),
    error:    { background:'#FBEEEB', border:`1px solid #E8C4BC`, borderLeft:`3px solid ${N.red}`, borderRadius:9, padding:'11px 14px', fontSize:12.5, color:N.red, marginTop:10, lineHeight:1.5 },

    tag:      (actief) => ({ display:'inline-block', padding:'7px 15px', borderRadius:8, fontSize:13.5, fontWeight:600, cursor:'pointer', margin:'0 8px 8px 0', border:`1px solid ${actief ? N.accent : N.line2}`, background: actief ? '#EAF2F3' : N.paper, color: actief ? N.accent : N.ink3, userSelect:'none' }),

    uitleg:   { background:'#F2F7F8', border:'1px solid #D3E3E6', borderLeft:`3px solid ${N.accent2}`, borderRadius:10, padding:'14px 16px', marginTop:16 },
    uitlegT:  { fontSize:13, fontWeight:700, color:N.accent, marginBottom:7 },
    uitlegR:  { fontSize:12.5, color:N.ink2, lineHeight:1.7, display:'flex', gap:8 },

    wKop:     { display:'flex', justifyContent:'space-between', alignItems:'center', margin:'22px 0 11px', paddingTop:18, borderTop:`1px solid ${N.line}` },
    wKopT:    { fontSize:14, fontWeight:700, color:N.ink },
    wKopSom:  { fontSize:12.5, color:N.ink4, marginLeft:8, fontWeight:400 },

    wijkCard: (actief) => ({ background:N.paper2, border:`1px solid ${N.line2}`, borderRadius:10, padding:'14px 16px', marginBottom:10, opacity: actief ? 1 : 0.5 }),
    wRij:     { display:'flex', alignItems:'center', gap:10, marginBottom:11 },
    wNaam:    { flex:1, background:N.paper, border:`1px solid ${N.line2}`, borderRadius:7, padding:'7px 10px', fontSize:14, fontWeight:600, color:N.ink, fontFamily:'inherit', outline:'none' },
    wId:      { fontSize:11, fontWeight:600, color:N.ink4, background:N.head, border:`1px solid ${N.line}`, padding:'3px 8px', borderRadius:5, whiteSpace:'nowrap' },
    wAct:     { display:'flex', alignItems:'center', gap:7, fontSize:12.5, color:N.ink3, whiteSpace:'nowrap' },
    pil:      (aan) => ({ width:36, height:20, borderRadius:10, background: aan ? N.accent2 : N.line2, position:'relative', cursor:'pointer', transition:'background 0.16s', flexShrink:0 }),
    knb:      (aan) => ({ position:'absolute', top:3, left: aan ? 19 : 3, width:14, height:14, borderRadius:'50%', background:'#fff', transition:'left 0.16s', boxShadow:'0 1px 2px rgba(0,0,0,0.2)' }),
    label2:   { fontSize:11.5, fontWeight:600, color:N.ink3, marginBottom:5, display:'block' },
    smi:      { width:'100%', boxSizing:'border-box', background:N.paper, border:`1px solid ${N.line2}`, borderRadius:7, padding:'7px 10px', fontSize:13.5, color:N.ink, fontFamily:'inherit', outline:'none', fontVariantNumeric:'tabular-nums' },

    grid2:    { display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 },

    samenv:   { background:N.head, border:`1px solid ${N.line}`, borderRadius:10, padding:'16px 18px', marginBottom:16 },
    samenvN:  { fontSize:17, fontWeight:700, color:N.ink, marginBottom:12, letterSpacing:'-0.02em' },
    samenvG:  { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'9px 20px' },
    samenvI:  { fontSize:13, display:'flex', justifyContent:'space-between', padding:'4px 0', borderBottom:'1px solid #EDEAE4' },
    wRij2:    { display:'flex', justifyContent:'space-between', padding:'9px 12px', background:N.paper2, border:`1px solid ${N.line}`, borderRadius:7, marginBottom:5, fontSize:13 },
    klaarBlok:{ background:N.okBg, border:`1px solid ${N.okBd}`, borderLeft:`3px solid ${N.ok}`, borderRadius:10, padding:'14px 16px', marginTop:16 },
    klaarR:   { fontSize:12.5, color:N.ink2, lineHeight:1.75, display:'flex', gap:8 },
  };

  // ── Stappenbalk ───────────────────────────────────────────────────
  const Progress = () => (
    <div style={s.steps}>
      {STAPPEN.map((naam, i) => (
        <button key={i} style={s.step(i===stap, i<stap)}
          onClick={() => i < stap && setStap(i)}>
          <span style={s.bol(i===stap, i<stap)}>{i < stap ? '✓' : i+1}</span>
          {naam}
        </button>
      ))}
    </div>
  );

  const totaalInwoners = wijken.filter(w=>w.actief).reduce((s,w) => s + (parseInt(w.inwoners)||0), 0);
  const actieveWijken  = wijken.filter(w=>w.actief);

  // Voetnoot en knoptekst per stap
  const voetteksten = [
    'Typ een gemeentenaam en druk op Enter, of klik Zoeken',
    // Bij stap 2 stond hier het aantal wijken en de herkomst. Dat staat al in
    // het kader bovenaan die stap, dus het voegde niets toe. Leeg, zoals bij
    // de stappen erna.
    '',
    'Het wijktype bepaalt de verdeling over bewoners, bezoekers en logistiek',
    '',
    'Klaar om toe te voegen',
  ];

  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={s.modal}>

        <div style={s.header}>
          <div>
            <div style={s.title}>Nieuwe gemeente toevoegen</div>
            <div style={s.subtitle}>Gegevens worden automatisch opgehaald, jij controleert en stelt bij waar nodig</div>
          </div>
          <button style={s.close} onClick={onClose}>✕</button>
        </div>

        <Progress />

        <div style={s.body}>

          {/* ══ STAP 0: GEMEENTE ZOEKEN ══════════════════════════════ */}
          {stap === 0 && (
            <div>
              <div style={s.row}>
                <label style={s.label}>Gemeentenaam</label>
                <input style={s.input} value={zoekNaam}
                  onChange={e => setZoekNaam(e.target.value)}
                  onKeyDown={e => e.key==='Enter' && zoekGemeente()}
                  placeholder="bijvoorbeeld Mechelen, Hasselt of Breda" autoFocus />
              </div>
              <div style={s.row}>
                <label style={s.label}>Land</label>
                <div>
                  {['België','Nederland'].map(l => (
                    <div key={l} style={s.tag(land===l)} onClick={() => setLand(l)}>{l}</div>
                  ))}
                </div>
              </div>

              {error && <div style={s.error}>{error}</div>}

              <div style={s.uitleg}>
                <div style={s.uitlegT}>Wat wordt automatisch opgehaald?</div>
                <div style={s.uitlegR}><span style={{color:N.ok,fontWeight:700}}>✓</span>Wijken, inwoners en auto&rsquo;s per wijk, uit de statistische sectoren van Statbel</div>
                <div style={s.uitlegR}><span style={{color:N.ok,fontWeight:700}}>✓</span>Oppervlakte en middelpunt van elke wijk</div>
                <div style={{ ...s.uitlegR, marginTop:6 }}><span style={{color:N.warn,fontWeight:700}}>!</span>Wijktype en privé-aandeel stel jij zelf in</div>
              </div>
            </div>
          )}

          {/* ══ STAP 1: WIJKEN EN GEGEVENS ═══════════════════════════ */}
          {stap === 1 && (
            <div>
              {/* Waar de wijken vandaan komen. Zonder deze regel is niet te zien
                  of een nul een meting is of een ontbrekend gegeven. */}
              {indelingBron && (
                <div style={{ fontSize:13.5, lineHeight:1.55, marginBottom:14, padding:'10px 12px',
                              background:'#EEF5F2', border:'1px solid #BFD8CE', borderRadius:8,
                              color:N.ink2 }}>
                  <b>{indelingBron.aantal} wijken uit de gemeentegedragen wijkindeling</b>,
                  opgebouwd uit {indelingBron.sectoren} statistische sectoren.
                  {indelingBron.inwonersBekend
                    ? ' Inwoners en oppervlakte zijn gemeten uit die sectoren.'
                    : ' LET OP: het inwonertal per sector is niet ingelezen, dus de inwoners staan op nul.'}
                  {indelingBron.autosBekend
                    ? ' De voertuigen zijn over de wijken verdeeld naar het aantal auto\u2019s per sector.'
                    : ' De autotelling per sector is niet ingelezen, dus de voertuigen zijn naar inwonertal verdeeld.'}
                </div>
              )}
              {indelingMelding && (
                <div style={{ fontSize:13.5, lineHeight:1.55, marginBottom:14, padding:'10px 12px',
                              background:'#FBF3E2', border:'1px solid #E4D2A8', borderRadius:8,
                              color:N.ink2 }}>
                  {indelingMelding} De wijken hieronder komen daarom uit OpenStreetMap, met
                  inwoners en voertuigen gelijk verdeeld. Controleer ze.
                </div>
              )}
              <div style={s.grid2}>
                <div style={s.row}>
                  <label style={s.label}>Gemeente</label>
                  <input style={s.input} value={gemeenteNaam} onChange={e => setGemeenteNaam(e.target.value)} />
                </div>
                <div style={s.row}>
                  <label style={s.label}>Centrum coördinaten</label>
                  <input style={{ ...s.input, background:N.head, color:N.ink3 }} readOnly
                    value={geoData ? `${geoData.center[0].toFixed(4)}, ${geoData.center[1].toFixed(4)}` : ''} />
                </div>
              </div>

              <div style={s.grid2}>
                <div style={s.row}>
                  <label style={s.label}>Totaal inwoners</label>
                  <input style={inwonersIsSchatting ? s.inputLet : s.input} type="number"
                    value={inwoners} onChange={e => { setInwoners(e.target.value); setInwonersIsSchatting(false); }} />
                  {inwonersIsSchatting ? (
                    <div style={s.bron('let')}>
                      <span>!</span>
                      <span>Schatting op basis van de oppervlakte. Geen officiële bevolkingsopgave; controleer en corrigeer dit.</span>
                    </div>
                  ) : (
                    <div style={s.bron('ok')}>
                      <span>✓</span>
                      <span>Wikidata, meest recente bevolkingsopgave</span>
                    </div>
                  )}
                </div>
                <div style={s.row}>
                  <label style={s.label}>Totaal voertuigen</label>
                  <input style={voertuigenHerkomst ? s.inputVast : s.inputLet} type="number"
                    value={voertuigen} readOnly={Boolean(voertuigenHerkomst)}
                    onChange={e => { if (!voertuigenHerkomst) setVoertuigen(e.target.value); }} />
                  {voertuigenHerkomst ? (
                    <div style={s.bron('ok')}>
                      <span>✓</span>
                      <span>{voertuigenHerkomst}</span>
                    </div>
                  ) : (
                    <div style={s.bron('let')}>
                      <span>!</span>
                      <span>
                        Schatting op basis van 44 procent van het inwonertal, de gemiddelde verhouding
                        uit onze geverifieerde gemeenten. Dit is geen cijfer van DIV of Febiac;
                        controleer en corrigeer het handmatig.
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div style={s.grid2}>
                <div style={s.row}>
                  <label style={s.label}>Privé-aandeel, berekend</label>
                  <input style={s.input} type="number" min="0" max="100"
                    value={privePct} onChange={e => setPrivePct(e.target.value)} />
                  <div style={s.bron('neutraal')}>
                    Uit de Stadsmonitor, private buitenruimte, of een eigen bron als die er is.
                  </div>
                </div>
              </div>

              <div style={s.wKop}>
                <div>
                  <span style={s.wKopT}>Wijken</span>
                  <span style={s.wKopSom}>
                    {actieveWijken.length} actief · samen {totaalInwoners.toLocaleString('nl-BE')} inwoners
                  </span>
                </div>
                <button style={s.btn(true)} onClick={addWijk}>Wijk toevoegen</button>
              </div>

              {wijken.map((wijk, idx) => (
                <div key={wijk.id} style={s.wijkCard(wijk.actief)}>
                  <div style={s.wRij}>
                    <input style={s.wNaam} value={wijk.naam}
                      onChange={e => updateWijk(idx, 'naam', e.target.value)} />
                    <span style={s.wId}>{wijk.id}</span>
                    <div style={s.wAct}>
                      actief
                      <div style={s.pil(wijk.actief)} onClick={() => toggleWijk(idx)}>
                        <div style={s.knb(wijk.actief)} />
                      </div>
                    </div>
                  </div>
                  <div style={s.grid2}>
                    <div>
                      <label style={s.label2}>Inwoners</label>
                      <input style={s.smi} type="number" value={wijk.inwoners}
                        onChange={e => updateWijk(idx, 'inwoners', e.target.value)} />
                    </div>
                    <div>
                      <label style={s.label2}>Voertuigen</label>
                      <input style={s.smi} type="number" value={wijk.voertuigen}
                        onChange={e => updateWijk(idx, 'voertuigen', e.target.value)} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ══ STAP 2: WIJKTYPE ═════════════════════════════════════ */}
          {stap === 2 && (
            <div>
              <div style={{ ...s.uitleg, marginTop:0, marginBottom:18 }}>
                <div style={s.uitlegT}>Waarom dit uitmaakt</div>
                <div style={{ fontSize:12.5, color:N.ink2, lineHeight:1.7 }}>
                  Het wijktype bepaalt de verhouding tussen bewoners, bezoekers en logistiek in een
                  wijk, en daarmee hoeveel laadpunten er nodig zijn. Vink meerdere types aan voor
                  een gemengde wijk, bijvoorbeeld wonen en werken; het model middelt dan de
                  bijbehorende verhoudingen. Openbaar vervoer telt alleen mee als je zelf een
                  percentage invult, want bussen laden meestal op een eigen depot en niet op straat.
                </div>
              </div>

              {actieveWijken.map((wijk) => {
                const idx = wijken.indexOf(wijk);
                return (
                  <div key={wijk.id} style={s.wijkCard(true)}>
                    <div style={{ fontSize:14, fontWeight:700, color:N.ink, marginBottom:11 }}>{wijk.naam}</div>

                    <label style={s.label2}>Wijktype</label>
                    <div style={{ marginBottom:6 }}>
                      {WIJKTYPES.map(t => (
                        <div key={t.key} style={s.tag((wijk.wijktype||[]).includes(t.key))}
                          onClick={() => toggleWijktype(idx, t.key)}>
                          {t.label}
                        </div>
                      ))}
                    </div>

                  </div>
                );
              })}
            </div>
          )}

          {/* ══ STAP 3: GIS NETBEHEERDER ═════════════════════════════ */}
          {stap === 3 && (
            <div>
              <div style={{ fontSize:14, fontWeight:700, color:N.ink, marginBottom:10 }}>GIS netbeheerder</div>
              <input ref={gisKiezer} type="file" style={{ display:'none' }}
                onChange={async e => {
                  const b = e.target.files?.[0];
                  e.target.value = '';
                  if (!b) return;
                  if (!(await isGeoPackage(b))) { setGisFout('Kies een GeoPackage-bestand (.gpkg).'); return; }
                  setGisFout(''); setGisBestand(b);
                }} />
              <div style={{ border:`2px dashed ${N.line2}`, borderRadius:10, padding:18, textAlign:'center',
                            color:N.ink3, fontSize:14 }}>
                {gisBestand
                  ? `${gisBestand.name} gekozen. Wordt ingelezen zodra de gemeente is toegevoegd.`
                  : 'Het GeoPackage (.gpkg) komt van de netbeheerder.'}
                <div style={{ display:'flex', gap:8, justifyContent:'center', marginTop:12 }}>
                  <button style={s.btn(true)} onClick={() => { setGisFout(''); gisKiezer.current?.click(); }}>Bestand kiezen</button>
                  <button style={s.btn(false)} onClick={() => { setGisBestand(null); setGisFout(''); setStap(4); }}>Later</button>
                </div>
              </div>
              {gisFout && <div style={{ fontSize:12.5, color:'#B4442F', fontWeight:600, marginTop:8 }}>{gisFout}</div>}
              <div style={{ fontSize:12.5, color:N.ink3, marginTop:8 }}>Kan ook later worden toegevoegd via Bewerken.</div>
            </div>
          )}

          {/* ══ STAP 4: BEVESTIGING ══════════════════════════════════ */}
          {stap === 4 && (
            <div>
              <div style={s.samenv}>
                <div style={s.samenvN}>{gemeenteNaam}</div>
                <div style={s.samenvG}>
                  {/* Coördinaten en begrenzing staan hier bewust niet: die
                      gebruikt de app alleen intern om de kaart te positioneren
                      en de wijken op te halen, er valt niets aan te controleren. */}
                  {[
                    ['Land',           land],
                    ['Actieve wijken', actieveWijken.length],
                    ['Inwoners',       (parseInt(inwoners)||0).toLocaleString('nl-BE')],
                    ['Voertuigen',     (parseInt(voertuigen)||0).toLocaleString('nl-BE')],
                    ['Privé-aandeel',  `${privePct}%`],
                  ].map(([l,v]) => (
                    <div key={l} style={s.samenvI}>
                      <span style={{ color:N.ink3 }}>{l}</span>
                      <span style={{ color:N.ink, fontWeight:600, fontVariantNumeric:'tabular-nums' }}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ fontSize:13, fontWeight:700, color:N.ink, marginBottom:9 }}>
                Wijken die worden aangemaakt
              </div>
              {actieveWijken.map(wijk => (
                <div key={wijk.id} style={s.wRij2}>
                  <span style={{ fontWeight:600, color:N.ink }}>{wijk.naam}</span>
                  <span style={{ color:N.ink3 }}>
                    {(parseInt(wijk.inwoners)||0).toLocaleString('nl-BE')} inwoners
                    {' · '}{(wijk.wijktype||['woonwijk']).join(' en ')}
                  </span>
                </div>
              ))}

              <div style={s.klaarBlok}>
                <div style={s.klaarR}><span style={{color:N.ok,fontWeight:700}}>✓</span>Na bevestiging staat de gemeente direct in het overzicht.</div>
                <div style={s.klaarR}><span style={{color:N.ok,fontWeight:700}}>✓</span>Bestaande laadpunten worden live opgehaald uit de MOW-dataset.</div>
                <div style={s.klaarR}><span style={{color:N.ok,fontWeight:700}}>✓</span>De rekenparameters gelden meteen voor alle wijken.</div>
              </div>
            </div>
          )}

        </div>{/* einde body */}

        <div style={s.footer}>
          <div style={{ fontSize:12.5, color:N.ink3 }}>{voetteksten[stap]}</div>
          <div style={{ display:'flex', gap:10 }}>
            {stap > 0 && (
              <button style={s.btn(false)} onClick={() => setStap(s => s-1)}>Terug</button>
            )}
            {stap === 0 && (
              <button style={s.btn(true)} onClick={zoekGemeente} disabled={loading}>
                {loading ? 'Zoeken…' : 'Zoeken'}
              </button>
            )}
            {stap > 0 && stap < 4 && (
              <button style={s.btn(true)} onClick={() => setStap(s => s+1)}>Volgende</button>
            )}
            {stap === 4 && (
              <button style={s.btn(true)} onClick={bevestig}>Gemeente toevoegen</button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
